import { buildSettleTrialPayload } from "@decibeltrade/sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAnalytics } from "@/contexts/analytics-context";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import type { FftSettleEventProps } from "@/lib/analytics";
import { Logger } from "@/utils/logger";

import { readDexSdk } from "../../dex-sdk";
import { fundedTrialEligibilityQueryKey } from "../../queries/funded-trial/eligibility";
import { trialHistoryBaseQueryKey } from "../../queries/funded-trial/trial-history";
import { usePrimarySessionKeyDex } from "../../write-dex-sdk-context";
import { useWalletFeePayerWithFallback } from "../wallet-fee-payer";
import { requireCampaignAddrs } from "./campaign-addrs";

export interface SettleTrialArgs extends FftSettleEventProps {
  trialId: number;
}

/**
 * User-side backup for the settle keeper — `settle_trial` is permissionless,
 * so the primary-subaccount session key signs silently when one exists.
 * Can abort on-chain when the close IOC can't fully fill; retrying is safe.
 */
export function useSettleTrial() {
  const queryClient = useQueryClient();
  const { accountAddress } = useDecibelWallet();
  const primaryDex = usePrimarySessionKeyDex();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const analytics = useAnalytics();

  return useMutation({
    mutationFn: async (args: SettleTrialArgs) => {
      analytics.fft.settleInitiated(args);

      if (!accountAddress) {
        throw new Error("Wallet not connected");
      }

      if (primaryDex) {
        try {
          const committed = await primaryDex.settleFftTrial({
            ...requireCampaignAddrs(),
            trialId: args.trialId,
          });
          return { txHash: committed.hash };
        } catch (error) {
          Logger.warn("Session-key settle-trial failed, falling back to wallet", {
            extra: { error },
          });
        }
      }

      const data = buildSettleTrialPayload({ ...requireCampaignAddrs(), trialId: args.trialId });

      const pending = await signAndSubmitWalletTransaction({ data });
      await readDexSdk.deps.aptos.waitForTransaction({ transactionHash: pending.hash });
      return { txHash: pending.hash };
    },
    onSuccess: ({ txHash }, args) => {
      analytics.fft.settleSucceeded({ ...args, txHash });
      if (!accountAddress) return;
      void queryClient.invalidateQueries({
        queryKey: fundedTrialEligibilityQueryKey({ account: accountAddress }),
      });
      void queryClient.invalidateQueries({
        queryKey: trialHistoryBaseQueryKey({ account: accountAddress }),
      });
    },
    onError: (error, args) => {
      analytics.fft.settleFailed({ ...args, error });
      Logger.error(error, { tags: { "fft.mutation": "settle" }, extra: { trialId: args.trialId } });
    },
  });
}
